import MovingArrow from '@assets/icons/arrow-left.svg?react'
import { useInfiniteScroll } from '@hooks'
import cn from 'classnames'
import { paths, useLazyGetPaymentAccountsFilteredQuery } from 'mmfintech-backend-api'
import type { AccountBalanceResponse } from 'mmfintech-backend-api/types'
import { isValidArray, tr, useDebounce } from 'mmfintech-commons'
import { type MouseEvent, useCallback, useEffect, useRef, useState } from 'react'
import { useHistory } from 'react-router-dom'

import { AccountCard } from './AccountCard'

import './AccountsSection.scss'

export const AccountsSection = () => {
  const {
    combinedData: activeAccounts,
    isFetching,
    readMore,
    scrollMore
  } = useInfiniteScroll(useLazyGetPaymentAccountsFilteredQuery, {
    filter: {
      accountStatuses: ['ACTIVE']
    },
    size: 20
  })

  const carouselRef = useRef(null)
  const [selectable, setSelectable] = useState<boolean>(true)
  const [selectedAccount, setSelectedAccount] = useState<AccountBalanceResponse>(null)
  const [canScrollLeft, setCanScrollLeft] = useState<boolean>(false)
  const [canScrollRight, setCanScrollRight] = useState<boolean>(false)
  const history = useHistory()

  const handleScroll = useDebounce(() => {
    typeof scrollMore === 'function' && scrollMore()
  }, 300)

  // Derive the edge fades/arrows from the real scroll position, so they stay correct when
  // the browser clamps scrollLeft on its own (e.g. the list shrinks or the viewport resizes).
  const syncScrollEdges = useCallback(() => {
    const el = carouselRef.current
    if (!el) return
    setCanScrollLeft(el.scrollLeft > 1)
    setCanScrollRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 1)
  }, [])

  const prev = () => {
    carouselRef.current?.scrollBy({ left: -400, behavior: 'smooth' })
  }

  const next = () => {
    carouselRef.current?.scrollBy({ left: 400, behavior: 'smooth' })
  }

  const onMouseDown = useCallback((e: MouseEvent<HTMLElement>) => {
    e.preventDefault()

    const drag = (mouseOverEvent: MouseEvent<HTMLElement>) => {
      document.body.style.cursor = 'pointer'
      setSelectable(false)
      carouselRef.current.scroll({
        left: carouselRef.current.scrollLeft - mouseOverEvent.movementX
      })
    }
    carouselRef.current.addEventListener('mousemove', drag)
    const cancelDrag = () => {
      carouselRef.current?.removeEventListener('mousemove', drag)
      document.body.style.cursor = 'default'
      setTimeout(() => {
        setSelectable(true)
      }, 200)
    }
    carouselRef.current.addEventListener('mouseup', cancelDrag, { once: true })
    carouselRef.current.addEventListener('mouseleave', cancelDrag, {
      once: true
    })
  }, [])

  const onScroll = useCallback(
    e => {
      e.preventDefault()
      syncScrollEdges()
      if (e.target.scrollLeft + e.target.clientWidth + 20 >= e.target.scrollWidth && !isFetching) {
        handleScroll()
      }
    },
    [syncScrollEdges, isFetching, handleScroll]
  )

  // biome-ignore lint/correctness/useExhaustiveDependencies: initial load only, must not refetch on rerender
  useEffect(() => {
    void readMore({ size: 20, page: 0 })
  }, [])

  useEffect(() => {
    const carouselElement = carouselRef.current
    if (!carouselElement) return

    carouselElement.addEventListener('mousedown', onMouseDown)
    carouselElement.addEventListener('scroll', onScroll)

    return () => {
      carouselElement.removeEventListener('mousedown', onMouseDown)
      carouselElement.removeEventListener('scroll', onScroll)
    }
  }, [onMouseDown, onScroll])

  useEffect(() => {
    const carouselElement = carouselRef.current
    if (!carouselElement) return

    const resizeObserver = new ResizeObserver(syncScrollEdges)
    resizeObserver.observe(carouselElement)

    return () => resizeObserver.disconnect()
  }, [syncScrollEdges])

  useEffect(() => {
    if (isValidArray(activeAccounts)) {
      setSelectedAccount(activeAccounts[0])
    }
    syncScrollEdges()
  }, [activeAccounts, syncScrollEdges])

  return (
    <div
      data-test='carousel-cards-main-test'
      className={cn('carousel-cards-main', {
        'left-blur': canScrollLeft,
        'right-blur': canScrollRight
      })}
    >
      <div className='carousel-cards-arrows-wrapper'>
        <span data-test='account-header-title-test' className='account-header-title'>
          {tr('FRONTEND.ACCOUNTS.MY_ACCOUNTS', 'My accounts')}
        </span>
        {isValidArray(activeAccounts) && activeAccounts.length > 2 && (
          <div className='carousel-arrows'>
            <div
              className={cn('carousel-cards-arrow', {
                active: canScrollLeft
              })}
              onClick={e => {
                e.preventDefault()
                prev()
              }}
            >
              <MovingArrow />
            </div>
            <div
              className={cn('carousel-cards-arrow rotated', {
                active: canScrollRight
              })}
              onClick={e => {
                e.preventDefault()
                next()
              }}
            >
              <MovingArrow />
            </div>
          </div>
        )}
      </div>
      <div data-test='carousel-cards-container-test' className='carousel-cards-container' ref={carouselRef}>
        {isValidArray(activeAccounts) &&
          activeAccounts.map(account => {
            return (
              <AccountCard
                key={account.id}
                account={account}
                active={account.id === selectedAccount?.id}
                onClick={() => {
                  if (selectable) {
                    history.push({
                      pathname: paths.banking.accounts.list(),
                      state: { accountId: account.id }
                    })
                  }
                }}
              />
            )
          })}
      </div>
    </div>
  )
}
